import {
  ErrorCode,
  adminUserListSchema,
  adminUserSchema,
  grantListSchema,
  grantSchema,
  purgeRequestLogsResultSchema,
  requestLogListSchema,
  resetLibraryResultSchema,
  type AdminUser,
  type CreateGrantRequest,
  type CreateUserRequest,
  type Grant,
  type RequestLogList,
  type RequestLogMethod,
  type RequestLogStatusClass,
  type ResetLibraryResult,
  type UpdateUserRequest,
} from '@xphoto/shared';
import { LONG_TIMEOUT_MS, apiFetch } from '../lib/http';

/**
 * Client typé du PANNEAU D'ADMINISTRATION (T7b, XPHOTO-25). Comme les autres
 * clients du front, chaque réponse est RE-PARSÉE via son schéma partagé
 * `@xphoto/shared` (« never trust the client », y compris ses propres réponses).
 *
 * Les cookies de session sont same-origin (`credentials: 'same-origin'`) : le
 * navigateur les joint automatiquement, aucun jeton n'est manipulé en JS.
 */

/** Erreur d'appel API : statut HTTP + code d'erreur normalisé (`CONFLICT`…). */
export class AdminApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'AdminApiError';
  }
}

/** `true` sur un conflit métier (e-mail pris, doublon, dernier superadmin). */
export function isConflict(err: unknown): boolean {
  return err instanceof AdminApiError && err.status === 409;
}

/**
 * Nature PRÉCISE d'un conflit 409 de création de compte : `email` (e-mail actif
 * déjà pris) ou `other` (409 sans code machine-lisible reconnu → message
 * générique). `null` si ce n'est pas un 409. Permet à l'UI d'afficher un message
 * ciblé sur l'e-mail.
 */
export function createUserConflictKind(err: unknown): 'email' | 'other' | null {
  if (!(err instanceof AdminApiError) || err.status !== 409) return null;
  if (err.code === ErrorCode.EMAIL_TAKEN) return 'email';
  return 'other';
}

/**
 * `true` si le conflit 409 est une AUTO-suppression refusée (`CANNOT_DELETE_SELF`).
 * Permet un message dédié côté client dans le cas d'une course (le bouton est
 * masqué sur sa propre ligne, mais l'API garde la vérité).
 */
export function isCannotDeleteSelf(err: unknown): boolean {
  return (
    err instanceof AdminApiError && err.status === 409 && err.code === ErrorCode.CANNOT_DELETE_SELF
  );
}

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON (ou 204 sans corps) : code générique.
  }
  return 'UNKNOWN';
}

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) throw new AdminApiError(res.status, await errorCodeOf(res));
  return res.json();
}

async function assertOk(res: Response): Promise<void> {
  if (!res.ok) throw new AdminApiError(res.status, await errorCodeOf(res));
}

// ---------------------------------------------------------------------------
// Comptes (superadmin)
// ---------------------------------------------------------------------------

/** `GET /api/admin/users` — liste des comptes (superadmin). */
export async function fetchUsers(signal?: AbortSignal): Promise<AdminUser[]> {
  const res = await apiFetch('/api/admin/users', {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  return adminUserListSchema.parse(await readJson(res)).users;
}

/** `POST /api/admin/users` — crée un compte ; 409 si l'e-mail est déjà pris. */
export async function createUser(input: CreateUserRequest): Promise<AdminUser> {
  const res = await apiFetch('/api/admin/users', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return adminUserSchema.parse(await readJson(res));
}

/** `PATCH /api/admin/users/:id` — met à jour affichage/rôle. */
export async function updateUser(id: string, patch: UpdateUserRequest): Promise<AdminUser> {
  const res = await apiFetch(`/api/admin/users/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return adminUserSchema.parse(await readJson(res));
}

/** `DELETE /api/admin/users/:id` — supprime un compte ; 409 si dernier superadmin. */
export async function deleteUser(id: string): Promise<void> {
  const res = await apiFetch(`/api/admin/users/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  await assertOk(res);
}

// ---------------------------------------------------------------------------
// Accès (grants lecture seule)
// ---------------------------------------------------------------------------

/** `GET /api/libraries/:id/grants` — accès de la bibliothèque (propriétaire ou superadmin). */
export async function fetchGrants(libraryId: string, signal?: AbortSignal): Promise<Grant[]> {
  const res = await apiFetch(`/api/libraries/${libraryId}/grants`, {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  return grantListSchema.parse(await readJson(res)).grants;
}

/** `POST /api/libraries/:id/grants` — accorde un accès ; 404 user inconnu, 409 doublon. */
export async function createGrant(libraryId: string, input: CreateGrantRequest): Promise<Grant> {
  const res = await apiFetch(`/api/libraries/${libraryId}/grants`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return grantSchema.parse(await readJson(res));
}

/** `DELETE /api/libraries/:id/grants/:grantId` — révoque un accès. */
export async function deleteGrant(libraryId: string, grantId: string): Promise<void> {
  const res = await apiFetch(`/api/libraries/${libraryId}/grants/${grantId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  await assertOk(res);
}

// ---------------------------------------------------------------------------
// Journaux de requêtes (superadmin)
// ---------------------------------------------------------------------------

/** Filtres optionnels de la barre de filtres des journaux de requêtes (Lot C). */
export interface RequestLogsFilters {
  userId?: string | undefined;
  method?: RequestLogMethod | undefined;
  statusClass?: RequestLogStatusClass | undefined;
}

/** `GET /api/admin/request-logs` — page de journaux filtrée (superadmin). */
export async function fetchRequestLogs(
  limit: number,
  offset: number,
  filters: RequestLogsFilters = {},
  signal?: AbortSignal,
): Promise<RequestLogList> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (filters.userId !== undefined) params.set('userId', filters.userId);
  if (filters.method !== undefined) params.set('method', filters.method);
  if (filters.statusClass !== undefined) params.set('statusClass', filters.statusClass);
  const res = await apiFetch(`/api/admin/request-logs?${params.toString()}`, {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  return requestLogListSchema.parse(await readJson(res));
}

/**
 * `DELETE /api/admin/request-logs` — purge le journal (superadmin). Sans `before`
 * → purge tout ; avec `before` (ISO 8601) → purge les entrées antérieures. Renvoie
 * le nombre d'entrées supprimées.
 */
export async function purgeRequestLogs(before?: string): Promise<number> {
  const res = await apiFetch('/api/admin/request-logs', {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(before !== undefined ? { before } : {}),
  });
  return purgeRequestLogsResultSchema.parse(await readJson(res)).deleted;
}

// ---------------------------------------------------------------------------
// Réinitialisation d'une bibliothèque (superadmin)
// ---------------------------------------------------------------------------

/**
 * `POST /api/libraries/:id/reset` — réinitialise DESTRUCTIVEMENT une bibliothèque
 * (superadmin) : vide l'index des médias + supprime les vignettes. NE TOUCHE PAS
 * les fichiers originaux ni les EXIF sur disque (garanti côté serveur). Renvoie
 * les compteurs de ce qui a été supprimé.
 */
export async function resetLibrary(libraryId: string): Promise<ResetLibraryResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/reset`, {
    method: 'POST',
    credentials: 'same-origin',
    // Purge SYNCHRONE de l'index + des vignettes sur disque → plafond long.
    timeoutMs: LONG_TIMEOUT_MS,
  });
  return resetLibraryResultSchema.parse(await readJson(res));
}
